<script setup>
/**
 * The dialog a competition question is asked in: the one shell for the first
 * view of a competition (CompetitionQuestionsPrompt, switch starts off) and
 * for Turn on (SquashLevelsTurnOnDialog, switch starts on), so the two read
 * the same (owner, 3 Nov 2026, SQUASH_LEVELS_CARDS_PLAN.md).
 *
 * With ONE question waiting: its heading is the dialog's header text, its
 * logo sits big and centred on a white plate (no border, owner 3 Nov 2026;
 * the Squash Levels lettering is black, so the white stays in dark mode), then
 * the lead, the switch box, More info, the button (following the switch) and
 * the footnote under it. With several: "Before you start", each question with
 * its own logo and heading, and Save. Every word comes from the API via
 * competitionQuestionsDialog.
 *
 * Presentational: the parent fetches, saves, and decides what closing means
 * (a no on first view; nothing at all from Turn on). Escape closes like the X.
 */
import {computed} from 'vue';
import {competitionQuestionsDialog} from '~/composables/useCompetitionQuestions';
import CompetitionQuestionList from '~/components/competitions/CompetitionQuestionList.vue';

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  questions: {type: Array, default: () => []},
  ticked: {type: Object, default: () => ({})},
  title: {type: String, default: null},
  saving: {type: Boolean, default: false},
  loading: {type: Boolean, default: false},
  error: {type: String, default: null},
});
const emit = defineEmits(['update:modelValue', 'update:ticked', 'save']);

const dialog = computed(() => competitionQuestionsDialog({title: props.title}, props.questions, props.ticked));

const onModel = (value) => {
  if (!value) emit('update:modelValue', false);
};
</script>

<template>
  <tl-dialog :model-value="modelValue" @update:model-value="onModel" :title="dialog.heading" :max-width="440"
             :persistent="true" :close-on-escape="true">
    <div class="cqp">
      <div v-if="dialog.logo" class="cqp-biglogo" data-testid="competition-questions-logo">
        <img :src="dialog.logo.src" :alt="dialog.logo.alt" :width="dialog.logo.width" :height="dialog.logo.height"
             class="cqp-biglogo-img"/>
      </div>
      <div v-if="loading" class="cqp-loading"><v-progress-circular indeterminate size="24" color="primary"/></div>
      <competition-question-list v-else :model-value="ticked" @update:model-value="(v) => emit('update:ticked', v)"
                                 :questions="questions" :disabled="saving"
                                 :show-logo="!dialog.single" :show-heading="!dialog.single"/>
      <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mt-2">{{ error }}</v-alert>
      <div v-if="questions.length" class="cqp-actions">
        <v-btn color="primary" variant="flat" :loading="saving" data-testid="competition-questions-save"
               @click="emit('save')">{{ dialog.saveLabel }}</v-btn>
      </div>
      <p v-if="dialog.footnote" class="cqp-footnote" data-testid="competition-questions-footnote">{{ dialog.footnote }}</p>
    </div>
  </tl-dialog>
</template>

<style scoped lang="scss">
.cqp { display: flex; flex-direction: column; gap: 10px; }
/* Big and centred, on white in both themes (the lettering is black), and no
   border round it (owner, 3 Nov 2026). */
.cqp-biglogo {
  align-self: center;
  background: #fff;
  border: 0;
  border-radius: 16px;
  padding: 14px 18px;
  display: flex;
  justify-content: center;
  width: min(260px, 100%);
}
.cqp-biglogo-img { width: 100%; max-width: 220px; height: auto; display: block; }
.cqp-loading { display: flex; justify-content: center; padding: 16px 0; }
.cqp-actions { display: flex; justify-content: flex-end; margin-top: 6px; }
.cqp-footnote { margin: 0; text-align: center; font-size: 12px; line-height: 1.4; color: rgb(var(--v-theme-textSubtle)); }
</style>
